import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Upload, CheckCircle, XCircle, Loader } from 'lucide-react';
import { importVocabulary } from '../services/vocabularyImport';
import { useToast } from '../hooks/use-toast';

const VocabularyImporter = () => {
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const handleImport = async () => {
    setImporting(true);
    setProgress(0);
    setImportResults(null);

    try {
      toast({
        title: "Starting Import",
        description: "Importing authentic Algerian vocabulary...",
      });

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 200);

      const results = await importVocabulary();
      
      clearInterval(progressInterval);
      setProgress(100);
      setImportResults(results);

      toast({
        title: "Import Complete!",
        description: `Successfully imported ${results.success} words with ${results.errors} errors.`,
      });

    } catch (error) {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="w-6 h-6 text-green-600" />
            <span>Import Authentic Algerian Vocabulary</span>
          </CardTitle>
          <p className="text-gray-600">
            Load 200+ carefully curated Algerian dialect words with pronunciations and examples
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Import Button */}
          <div className="text-center">
            <Button 
              onClick={handleImport}
              disabled={importing}
              size="lg"
              className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white px-8 py-3"
            >
              {importing ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Import Vocabulary
                </>
              )}
            </Button>
          </div>

          {/* Progress Bar */}
          {importing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Importing vocabulary...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-3" />
            </div>
          )}

          {/* Import Results */}
          {importResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-green-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-600">{importResults.success}</div>
                    <div className="text-sm text-gray-600">Successful</div>
                  </CardContent>
                </Card>
                
                <Card className="border-red-200">
                  <CardContent className="p-4 text-center">
                    <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-red-600">{importResults.errors}</div>
                    <div className="text-sm text-gray-600">Errors</div>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200">
                  <CardContent className="p-4 text-center">
                    <Upload className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-600">{importResults.total}</div>
                    <div className="text-sm text-gray-600">Total Words</div>
                  </CardContent>
                </Card>
              </div>

              {/* Vocabulary Categories Preview */}
              <Card className="bg-green-50 border-green-200">
                <CardHeader>
                  <CardTitle className="text-lg">Imported Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-green-100 text-green-800">👋 Greetings</Badge>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">🤝 Introducing Yourself</Badge>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800">🌍 Countries & Ethnicities</Badge>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-800">🔢 Numbers</Badge>
                    <Badge variant="secondary" className="bg-red-100 text-red-800">👨‍👩‍👧‍👦 Family</Badge>
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">🥘 Food</Badge>
                    <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">💬 Everyday Phrases</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Error Details */}
              {importResults.errors > 0 && (
                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-700">Import Errors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {importResults.results
                        .filter(result => result.status === 'error')
                        .map((result, index) => (
                          <div key={index} className="text-sm text-red-600">
                            <strong>{result.word}:</strong> {result.error}
                          </div>
                        ))
                      }
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Next Steps */}
          {importResults && importResults.success > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h3 className="font-semibold text-blue-800 mb-2">🎉 Import Successful!</h3>
                <p className="text-blue-700 text-sm mb-3">
                  Your authentic Algerian vocabulary has been imported. You can now:
                </p>
                <div className="space-y-1 text-sm text-blue-700">
                  <div>• Browse vocabulary in the Vocabulary Manager</div>
                  <div>• Play games with authentic Algerian content</div>
                  <div>• Track your progress across categories</div>
                  <div>• Add more vocabulary as needed</div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VocabularyImporter;